import { useState, useEffect, useRef } from 'react';

// 카메라 관련 훅
export const useCamera = () => {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // 카메라 스트림 정리 함수
  const stopCameraStream = () => {
    try {
      if (cameraStream) {
        const tracks = cameraStream.getTracks();
        tracks.forEach(track => {
          track.stop();
        });
        setCameraStream(null);
      }
      
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject = null;
      }
    } catch (error) {
      // 카메라 스트림 정리 오류 시 조용히 무시
    }
  };

  // 카메라 시작 함수
  const startCamera = async () => {
    try {
      // 기존 스트림이 있다면 먼저 정리
      if (cameraStream) {
        stopCameraStream();
        // 잠시 대기
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('이 브라우저는 카메라 기능을 지원하지 않습니다.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        } 
      });
      setCameraStream(stream);
      setIsCameraOpen(true);
      
    } catch (error) {
      let errorMessage = '카메라 오류가 발생했습니다.';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = '카메라 접근이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = '카메라를 찾을 수 없습니다. 카메라가 연결되어 있는지 확인해주세요.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = '카메라가 다른 애플리케이션에서 사용 중입니다.';
      }
      
      setIsCameraOpen(false);
      setIsVideoReady(false);
      throw new Error(errorMessage);
    }
  };

  // 사진 촬영 함수
  const capturePhoto = (onCapture) => {
    if (!videoRef.current || !canvasRef.current) {
      alert('카메라가 준비되지 않았습니다.');
      return;
    }

    if (!cameraStream) {
      alert('카메라 스트림이 없습니다.');
      return;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;
      
      canvas.width = width;
      canvas.height = height;
      
      const context = canvas.getContext('2d');
      if (!context) {
        alert('캔버스를 초기화할 수 없습니다.');
        return;
      }
      
      context.drawImage(video, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        if (!blob) {
          alert('사진 생성에 실패했습니다.');
          return;
        }
        
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        setIsCameraOpen(false);
      }, 'image/jpeg', 0.8);
      
    } catch (error) {
      alert(`사진 촬영 중 오류가 발생했습니다: ${error.message}`);
    }
  };

  // 비디오 이벤트 핸들러 설정
  useEffect(() => {
    if (isCameraOpen && cameraStream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = cameraStream;
      
      const handleLoadedMetadata = () => {
        video.play().catch(() => {
          // 비디오 재생 오류 시 조용히 무시
        });
      };
      
      const handleCanPlay = () => {
        setIsVideoReady(true);
      };
      
      const handlePlaying = () => {
        setIsVideoReady(true);
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('playing', handlePlaying);
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('playing', handlePlaying);
      };
    } else if (!isCameraOpen) {
      setIsVideoReady(false);
      stopCameraStream();
    }
  }, [isCameraOpen, cameraStream]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => stopCameraStream();
  }, []);

  return {
    isCameraOpen,
    setIsCameraOpen,
    isVideoReady,
    cameraStream,
    videoRef,
    canvasRef,
    startCamera,
    capturePhoto,
    stopCameraStream
  };
};
